/**
 * Trailing space that makes the **last** scroll position of a horizontally
 * scrolling table land on a column boundary.
 *
 * ## The defect this exists for
 *
 * A table with a `position: sticky` first column overlays that column on top of
 * whatever the scroll has brought under it. Anywhere in the middle of the scroll
 * that is harmless — a number half-hidden behind the pin can be uncovered by
 * nudging. At **maximum** scroll it cannot: `scrollLeft` clamps to
 * `scrollWidth - clientWidth`, and if that resting position happens to put the
 * pin's right edge inside a number, the reader has no way to see the rest of it.
 * A right-aligned `1,502` showing only its last two glyphs reads as `02`, which
 * is a perfectly plausible value in a numeric column — the reader is not warned,
 * they are misinformed.
 *
 * ## Why it is computed rather than chosen
 *
 * Where that resting position falls is a function of how wide the numbers happen
 * to be. R152 fixed it by flush-righting the figures, which cleared the pin *for
 * the three-digit `Days listed` values of that afternoon*; when the feed
 * roll-call halved the corpus, the median days-since-listed fell 110 → 49, the
 * column lost a digit, every column after it shifted, and `Median sqft` landed
 * on the pin's edge instead. The fix had been tuned to the data, so the data
 * moved and took it with it.
 *
 * This function is tuned to nothing. Given the geometry as it actually is —
 * measured from the DOM, at whatever width the digits are today — it returns the
 * smallest trailing gutter that moves the terminal scroll position onto a column
 * boundary, so the column under the pin is *entirely* under it and the next one
 * starts flush against its edge. For the live markets table at 390 that is 26 px.
 *
 * R148 considered trailing padding and rejected it at "84–106 px, a blank third
 * of a phone viewport". That was the cost of aligning the pin's edge to the
 * table's *end* (`viewport - pinWidth`), which hides every column. Aligning to
 * the *nearest* boundary is a fraction of that, and the guard below refuses the
 * trade if it ever isn't.
 */
export function terminalGutter(input: {
  /** The table's own width, excluding any gutter already applied. */
  content: number;
  /** The scroll container's client width. */
  viewport: number;
  /** Width of the pinned first column. */
  pinWidth: number;
  /** Left offset of every non-pinned column, measured from the row's left edge. */
  boundaries: number[];
}): number {
  const { content, viewport, pinWidth, boundaries } = input;

  // Nothing scrolls, so nothing can come to rest under the pin.
  if (content <= viewport || viewport <= 0 || pinWidth <= 0) return 0;

  // Where the pin's right edge lands, in table coordinates, at maximum scroll.
  const need = content - viewport + pinWidth;

  const next = boundaries.filter((b) => b >= need).sort((a, b) => a - b)[0];
  // The pin's edge is already inside the *last* column: the only boundary left is
  // the table's right edge, and aligning to it would blank the whole scrollport.
  if (next === undefined) return 0;

  const gutter = Math.ceil(next - need);
  // Never trade a sliced number for a screenful of nothing.
  return gutter >= viewport - pinWidth ? 0 : gutter;
}
